/* eslint-disable react/display-name */
// eslint-disable-next-line no-use-before-define
import React, { useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import { shallowEqual, useSelector } from 'react-redux';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';

import { AuthProviders } from 'modules/auth/config/authConfig';
import { persistPostAuthRedirectFromLocation } from 'utils/postAuthRedirect';
import validateRoute from 'utils/validateRoute';
import { getThemeContentPadding } from 'utils/theme';
import { hasMaintenanceWarning } from 'utils/showDetails';

import Footer from 'modules/_shared/components/Footer';
import Header from 'modules/_shared/components/Header';
import SideNav from 'modules/_shared/components/SideNav';
import Page403 from 'modules/_shared/pages/403';
import Notification from 'modules/_shared/components/Notification';
import CustomLoading from 'modules/_shared/components/CustomLoading';

const TENANT_LOGIN_ROUTES = {
  pwc: '/pwc-login',
  pld: '/pld-login',
  default: '/login',
};

const validateFullWidth = (fullWidth, isFullScreen) =>
  fullWidth || isFullScreen ? 'app-full-width' : '';

const isNotAuthenticated = (isLoading, token, user) =>
  !isLoading && !token && !user;

const selectAppState = (state) => ({
  theme: state.society?.actualSociety?.customization?.theme,
  fullWidth: state.fullWidth,
  notification: state.notification,
});

const GlobalNotification = ({ notification }) =>
  notification?.visible ? (
    <Notification
      id={notification.id}
      title={notification.title}
      message={notification.message}
      type={notification.type}
      link={notification.link}
      linkText={notification.linkText}
      icon={notification.icon}
      fill={notification.fill}
      dismissible={notification.dismissible}
      action={notification.action}
      actionText={notification.actionText}
      actionData={notification.actionData}
    />
  ) : null;

export const LegalRoute = ({
  id,
  path,
  component,
  user,
  isLoading,
  isFullScreen,
  isNewUI,
}) => {
  const location = useLocation();
  const history = useHistory();

  const { theme, fullWidth, notification } = useSelector(
    selectAppState,
    shallowEqual
  );

  const isFullWidth = useMemo(
    () => validateFullWidth(fullWidth, isFullScreen),
    [fullWidth, isFullScreen]
  );

  const isValidRoute = useMemo(
    () => validateRoute(user, id, location.pathname),
    [user, id, location.pathname]
  );

  // const RouteComponent = useMemo(
  //   () => withAuthenticationRequired(component),
  //   [component]
  // );

  const RouteComponent = useMemo(() => {
    if (process.env.REACT_APP_AUTH_PROVIDER === AuthProviders.AUTH0) {
      return withAuthenticationRequired(component, {
        onRedirecting: () => <CustomLoading />,
      });
    }

    if (process.env.REACT_APP_AUTH_PROVIDER === AuthProviders.COGNITO) {
      return (props) => React.createElement(component, props);
    }
    return component;
  }, [component, isLoading, user, isValidRoute]);

  useEffect(() => {
    if (isNotAuthenticated(isLoading, localStorage.token, user)) {
      persistPostAuthRedirectFromLocation(
        location.pathname,
        location.search || ''
      );
      const authTenant = Cookies.get('authTenant') || 'default';
      const loginRoute = TENANT_LOGIN_ROUTES[authTenant] || '/login';

      history.push(loginRoute);
    }
  }, [isLoading, user, history, location.pathname, location.search]);

  return user ? (
    <div className="app-content nk-wrap">
      <Header isFullScreen={isFullScreen} />
      <GlobalNotification notification={notification} />

      <div
        className={`nk-content ${isNewUI ? 'bg-lighter' : ''}`}
        style={{
          paddingTop: hasMaintenanceWarning()
            ? 0
            : getThemeContentPadding(theme),
          marginTop: hasMaintenanceWarning() ? 0 : '25px',
        }}
      >
        <div className={`container wide-xl d-flex ${isFullWidth}`}>
          <SideNav className="container__nav" />

          <div className="container__main">
            {isValidRoute ? (
              <Route path={path} component={RouteComponent} />
            ) : (
              <Route component={Page403} />
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  ) : (
    <CustomLoading />
  );
};
